<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';

type OrigemId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface OrigemRow {
  origem_id: number;
  origem_nome?: string;
  [key: string]: unknown;
}

const emit = defineEmits<{
  edit: [origemId: OrigemId];
  delete: [origemId: OrigemId];
}>();

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/config/origens/listaOrigens');

const tableColumns = computed(() => {
  return [
    {
      data: 'origem_id',
      title: t('ID'),
      width: 100,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'origem_nome',
      title: t('Nome'),
      width: 400,
      sortable: true,
      filterType: 'text'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `origens_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.origem_id;
      return id == null ? '' : `origens-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('OrigensTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: OrigemRow[]): void => {
  log('OrigensTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de origens'));
};

const handleDataError = (error: Error): void => {
  log('OrigensTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar origens') + ': ' + error.message);
};

const handleEditClick = (origemId: OrigemId): void => {
  log('OrigensTable', 'handleEditClick', 'Edit clicked for ID', origemId);
  emit('edit', origemId);
};

const onFilterUpdated = (): void => {
  log('OrigensTable', 'event:filter.updated', 'Received');
  loadData();
};

const onOrigemSaved = (): void => {
  log('OrigensTable', 'event:origem.saved', 'Received');
  loadData();
};

const onOrigemDeleted = (): void => {
  log('OrigensTable', 'event:origem.deleted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('OrigensTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('OrigensTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  // Listen for filter updates
  eventBus.on('filter.updated', onFilterUpdated);
  eventBus.on('origem.saved', onOrigemSaved);
  eventBus.on('origem.deleted', onOrigemDeleted);
});

onBeforeUnmount(() => {
  log('OrigensTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('filter.updated', onFilterUpdated);
  eventBus.off('origem.saved', onOrigemSaved);
  eventBus.off('origem.deleted', onOrigemDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
